// Pure presentation helpers for the Issues feature. No Angular/RxJS deps so
// these stay trivially unit-testable. Severity tier/colour reuse the existing
// Service Center helpers (single source of truth).

import { severityTier } from 'src/app/service-center/severity';
import { AssetFault } from 'src/app/models/assetFault';
import { IssueDto, IssueLifecycleState, IssueLifecycleStates, OPEN_LIFECYCLE_STATES } from './models/issue';
import { ContributionDto, ContributionSourceTypes } from './models/contribution';
import { IssueCaseDto } from './models/issue-case';
import { ActivityEntryDto, ActivityKinds } from './models/activity-entry';
import { StateReasonCode } from './models/state-history';
import { DispositionType } from './models/disposition';
import { ClosureReason } from './models/contribution';
import {
  EvidenceCardVm,
  EvidenceCountVm,
  EvidenceExpiryVm,
  EvidenceGroupVm,
  FaultStateLineVm,
  IssueRowVm,
  IssueTimelineEntryVm,
} from './models/issue-view';

// The "system" sentinel the backend stamps as the actor for automated entries.
const SYSTEM_ACTOR = 'system';

// Confidence at/above this is "Active"; below it renders muted.
export const ACTIVE_CONFIDENCE_THRESHOLD = 0.5;

// Max open rows shown in the widget before the "+N more" affordance.
export const WIDGET_TOP_N = 5;

// Lifecycle group render order (Closed never appears in the widget).
const GROUP_ORDER: IssueLifecycleState[] = [
  IssueLifecycleStates.Active, IssueLifecycleStates.Emerging, IssueLifecycleStates.Fading,
];

// A date string is "unset" when empty or an epoch-ish sentinel (year <= 1).
export function isUnsetDate(value: string | null | undefined): boolean {
  if (!value) return true;
  const t = Date.parse(value);
  if (isNaN(t)) return true;
  return new Date(t).getUTCFullYear() <= 1;
}

// Case workflow statusIds counted as "active" (New/Open/InProgress) — the same
// window the backend's activeForAsset lookup uses.
const ACTIVE_CASE_STATUS_IDS = [1, 2, 3];

// The ACTIVE linked case from the issue's denormalized case read-model, if any.
// Assignment now lives on the Case, not the Issue.
export function activeCaseOf(issue: IssueDto): IssueCaseDto | null {
  return (issue?.cases ?? [])
    .find(c => ACTIVE_CASE_STATUS_IDS.includes(c.status?.statusId)) ?? null;
}

export function isReopened(issue: IssueDto): boolean {
  return (issue?.reopenCount ?? 0) > 0;
}

// Green "DVIR" origin tag: the issue has at least one DVIR-sourced contribution.
// (No red "DVIR Not Safe" tag in the Issues UI — vehicle safety lives in the
// page header status strip.)
export function isDvirOrigin(issue: IssueDto): boolean {
  return (issue?.contributions ?? []).some(c => c.sourceType === ContributionSourceTypes.DVIRDefect);
}

export function confidencePct(confidence: number): number {
  return Math.round((confidence ?? 0) * 100);
}

// An OPEN issue never reads 0% — the operator-facing
// confidence is clamped to a 1% floor so a live-but-low issue still surfaces a
// value; a CLOSED issue shows its true (possibly 0) confidence.
export function displayConfidencePct(issue: IssueDto | null | undefined): number {
  const pct = confidencePct(issue?.confidence ?? 0);
  const open = OPEN_LIFECYCLE_STATES.includes(issue?.lifecycleState as IssueLifecycleState);
  return open ? Math.max(1, pct) : pct;
}

export function isConfidenceMuted(confidence: number): boolean {
  return (confidence ?? 0) < ACTIVE_CONFIDENCE_THRESHOLD;
}

// Initials from a display name, e.g. "J. Reyes" -> "JR", "Brad K." -> "BK".
export function initials(name: string | null | undefined): string | null {
  if (!name) return null;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  const letters = parts.map(p => p.replace(/[^A-Za-z0-9]/g, '').charAt(0)).filter(Boolean);
  if (!letters.length) return null;
  return (letters[0] + (letters.length > 1 ? letters[letters.length - 1] : '')).toUpperCase();
}

// Friendly translation keys -----------------------------------------------

const REASON_KEYS: Record<StateReasonCode, string> = {
  issue_created: '@issueReasonCreated',
  reopen: '@issueReasonReopened',
  confidence_crossed_active_threshold: '@issueReasonCrossedActive',
  confidence_dropped_below_active_threshold: '@issueReasonDroppedBelowActive',
  confidence_recovered_to_active_threshold: '@issueReasonRecoveredActive',
  dvir_repaired_auto_close: '@issueReasonDvirRepaired',
};

export function reasonKey(reason: string): string {
  return REASON_KEYS[reason as StateReasonCode] ?? '@issueReasonGeneric';
}

// Spent-based close causes, stamped onto Disposition.cause when an issue
// auto-closes: which fade clock spent the last live contribution.
const CLOSE_CAUSE_KEYS: Record<string, string> = {
  decay_spent_optime: '@issueCauseDecaySpentOpTime',
  decay_spent_backstop: '@issueCauseDecaySpentBackstop',
  decay_cleared: '@issueCauseDecayCleared',
};

const DISPOSITION_KEYS: Record<DispositionType, string> = {
  RealAndFixed: '@issueDispositionRealAndFixed',
  FalsePositive: '@issueDispositionFalsePositive',
  Dismissed: '@issueDispositionDismissed',
  Duplicate: '@issueDispositionDuplicate',
  AutoClosed: '@issueDispositionAutoClosed',
};

const CLOSURE_CHIP_KEYS: Record<ClosureReason, string> = {
  Decayed: '@issueClosureDecayed',
  DvirRepaired: '@issueClosureRepairedDvir',
  Cleared: '@issueClosureCleared',
  Expired: '@issueClosureExpired',
};

export function closureChipKey(reason: ClosureReason | ''): string | null {
  return reason ? CLOSURE_CHIP_KEYS[reason] ?? null : null;
}

// Lifecycle css class, consumed by the lifecycle chip and the sheet row's state cell.
export function lifecycleCss(state: IssueLifecycleState): string {
  return 'lc-' + (state ?? '').toLowerCase();
}

// Row / group building ------------------------------------------------------

export function buildRowVm(
  issue: IssueDto,
  resolveName: (userId: string) => string | null,
): IssueRowVm {
  const userId = activeCaseOf(issue)?.assignedUserId || null;
  const name = userId ? resolveName(userId) : null;
  const groups = groupEvidence(issue.contributions);
  return {
    issue,
    id: issue.id,
    displayName: issue.displayName,
    description: issue.description ?? '',
    lifecycleState: issue.lifecycleState,
    lifecycleCss: lifecycleCss(issue.lifecycleState),
    severity: issue.severity,
    confidencePct: displayConfidencePct(issue),
    confidenceMuted: isConfidenceMuted(issue.confidence),
    // The shared row renders the catalog's grouping from this name. Without it the chip
    // shows on the fleet sheet and not on the vehicle tab, and the same Issue reads as
    // two different things depending on where it is opened.
    category: issue.category,
    openedAtUtc: issue.createDateTimeUtc,
    lastActivityUtc: lastActivityUtc(issue),
    signalCount: groups.length,
    reopened: isReopened(issue),
    dvirOrigin: isDvirOrigin(issue),
    assigneeInitials: initials(name),
    assigneeName: name,
  };
}

// Most recent source report on the issue, with the creation time as fallback.
export function lastActivityUtc(issue: IssueDto): string {
  const times = (issue?.contributions ?? [])
    .map(c => c.lastFiredAt)
    .filter(t => !isUnsetDate(t))
    .map(t => Date.parse(t));
  if (!times.length) return issue?.createDateTimeUtc ?? '';
  return new Date(Math.max(...times)).toISOString();
}

// Top-N by severity tier (desc), reopened breaking ties first, then newest first.
export function topNBySeverity(rows: IssueRowVm[], n: number = WIDGET_TOP_N): IssueRowVm[] {
  return [...rows].sort(compareRowsForTruncation).slice(0, n);
}

export function compareRowsForTruncation(a: IssueRowVm, b: IssueRowVm): number {
  const tierDiff = severityTier(b.severity) - severityTier(a.severity);
  if (tierDiff !== 0) return tierDiff;
  const reopenDiff = (b.reopened ? 1 : 0) - (a.reopened ? 1 : 0);
  if (reopenDiff !== 0) return reopenDiff;
  return Date.parse(b.openedAtUtc || '') - Date.parse(a.openedAtUtc || '');
}

// Evidence card building ----------------------------------------------------

export function buildEvidenceCard(c: ContributionDto): EvidenceCardVm {
  const closed = !!c.closure?.reason;
  // The weight no longer renders. It still orders the cards and classifies decay.
  const decaying = !closed && (c.metrics?.currentDecayedWeight ?? 0) < (c.metrics?.baseWeight ?? 0);
  const manual = c.sourceType === ContributionSourceTypes.Manual;
  return {
    // An operator report's label is the operator's own summary line; only machine
    // sources fall back to the slug/rule identifier.
    displayLabel: manual ? (c.displayLabel || '') : (c.displayLabel || c.slug || c.ruleId || ''),
    source: c.sourceType,
    manual,
    metaLine: '', // composed in the component (fallback when evidenceText is empty)
    evidenceText: c.evidenceText || '',
    corroborationCount: c.corroborationCount ?? 0,
    severityAsserted: c.metrics?.severityAsserted ?? 0,
    codeLine: evidenceCodeLine(c),
    whenKey: '@issueEvidenceLastSeen',
    whenAtUtc: !isUnsetDate(c.lastCorroboratedAt) ? c.lastCorroboratedAt
      : !isUnsetDate(c.lastFiredAt) ? c.lastFiredAt : '',
    thresholdCount: c.evidence?.thresholdCount ?? 0,
    expiry: evidenceExpiry(c),
    decaying,
    decayReasonKey: decaying ? decayReasonKey(c) : null,
    lastFiredAtUtc: c.lastFiredAt,
    closed,
    closureChipKey: closureChipKey(c.closure?.reason ?? ''),
  };
}

// Evidence lines ------------------------------------------------------------

// The code a technician looks up, per protocol. J1939 and OBDII leave the
// description empty and Sensor is the only one that fills it, so Sensor leads
// with words where the others lead with numbers. OBDII needs the controller to
// form its letter, which the evidence does not carry, so it falls through to the
// generic form until the fault-state match supplies one.
export function evidenceCodeLine(c: ContributionDto): string {
  const faults = c.evidence?.faults ?? [];
  if (!faults.length) return '';
  const protocol = faults[0].protocol || '';
  const segments = faults.map(f => {
    switch (protocol) {
      case 'J1939': return `SPN ${f.faultCode} FMI ${f.failureMode}`;
      case 'J1708': return `PID/SID ${f.faultCode} FMI ${f.failureMode}`;
      case 'Sensor': return f.description || `Sensor fault ${f.faultCode}`;
      case 'Go': return `Code ${f.faultCode}`;
      default: return `Fault ${f.faultCode}`;
    }
  });
  const label = protocol === 'Go' ? 'GO' : protocol;
  return label ? `${segments.join(' + ')} · ${label}` : segments.join(' + ');
}

// Count source reports separately from corroborations that produce no new report.
export function evidenceCount(c: ContributionDto, occurrences: number): EvidenceCountVm {
  const since = isUnsetDate(c.firstFiredAt) ? '' : c.firstFiredAt;
  const reports = Math.max(occurrences ?? 0, 1);
  const key = reports === 1 ? '@issueEvidenceDetectedOnce' : '@issueEvidenceDetectedCount';
  return { key: since ? key + 'Since' : key, reports, sinceUtc: since };
}

// A DVIR defect stops counting on a hard expiry. An Alert carries
// DateTime.MaxValue, which arrives as year 9999.
export function evidenceExpiry(c: ContributionDto, now: Date = new Date()): EvidenceExpiryVm | null {
  if (c.sourceType !== ContributionSourceTypes.DVIRDefect) return null;
  const value = c.expiresAt;
  if (isUnsetDate(value)) return null;
  const at = Date.parse(value);
  if (isNaN(at) || new Date(at).getUTCFullYear() >= 9999) return null;
  if (at <= now.getTime()) return { key: '@issueEvidenceExpired', days: 0, atUtc: value };
  const days = Math.max(1, Math.round((at - now.getTime()) / 86400000));
  return days === 1
    ? { key: '@issueEvidenceExpiresInDay', days: 1, atUtc: value }
    : { key: '@issueEvidenceExpiresIn', days, atUtc: value };
}

// Whether the fault is still on the bus. The state record keys a row by
// protocol, controller, fault code, failure mode and fault type, and the
// evidence carries no controller, so one contribution can match several rows.
// Several matches resolve to nothing rather than to a guess.
export function matchFaultState(c: ContributionDto, faults: AssetFault[]): AssetFault | null {
  const evidence = (c.evidence?.faults ?? [])[0];
  if (!evidence || c.sourceType !== ContributionSourceTypes.Alert) return null;
  const hits = (faults ?? []).filter(f =>
    f.protocol === evidence.protocol
    && f.faultCode === evidence.faultCode
    && f.failureMode === evidence.failureMode);
  return hits.length === 1 ? hits[0] : null;
}

export const FAULT_STATE_ACTIVE = 2;
export const FAULT_STATE_INACTIVE = 3;

// The fault record types its dates as Date, and the wire sends strings. Take
// either and hand back an ISO string, or empty when the value is a sentinel.
function isoOrEmpty(value: Date | string | null | undefined): string {
  if (!value) return '';
  const t = value instanceof Date ? value.getTime() : Date.parse(value);
  if (isNaN(t)) return '';
  const at = new Date(t);
  return at.getUTCFullYear() <= 1 ? '' : at.toISOString();
}

export function faultStateLine(fault: AssetFault | null): FaultStateLineVm | null {
  if (!fault) return null;
  if (fault.faultState === FAULT_STATE_ACTIVE) {
    const at = isoOrEmpty(fault.latestDateTimeUtc);
    return at ? { key: '@issueEvidenceOnBus', atUtc: at } : null;
  }
  if (fault.faultState === FAULT_STATE_INACTIVE) {
    const at = isoOrEmpty(fault.lastActiveDateTimeUtc);
    return at ? { key: '@issueEvidenceLastActive', atUtc: at } : null;
  }
  return null;
}

// Best-effort decay reason. The exact phrasing isn't in the payload, so derive
// from the contribution's decay signals with a generic fallback.
export function decayReasonKey(c: ContributionDto): string {
  if (c.sourceType === ContributionSourceTypes.DVIRDefect && (c.consecutiveOmissions ?? 0) > 0) {
    return '@issueDecayOmittedDvir';
  }
  return '@issueDecayNoRefire';
}

// Grouping key for evidence: a recurring alert mints a fresh alertId each
// interval, so the backend appends a separate contribution per firing (it only
// merges exact same-alertId re-fires into fireCount). Collapse those back: Alerts
// group by ruleId (falling back to slug, then alertId, so empty-ruleId alerts
// don't all collapse together); DVIR defects group by slug (ruleId is empty).
// Operator reports never collapse — two people reporting the same system wrote
// two different accounts, and each one is its own evidence.
function evidenceGroupKey(c: ContributionDto): string {
  if (c.sourceType === ContributionSourceTypes.DVIRDefect) return `dvir:${c.slug}`;
  if (c.sourceType === ContributionSourceTypes.Manual) return `manual:${c.contributionId}`;
  return `alert:${c.ruleId || c.slug || c.sourceRefAlertId}`;
}

// Group contributions into one entry per distinct source. Alerts lead, then each kind
// sorts by the representative's current weight (desc). The representative is the
// most-recently-fired member (its decayed weight/severity reflect the live
// state); `occurrences` is the true firing count (Σ fireCount across the group);
// `hasLive` is true if any member is still counting (closure empty). Returns ALL
// groups — the caller slices for display and uses the full set for header counts.
export function groupEvidence(contributions: ContributionDto[]): EvidenceGroupVm[] {
  const groups = new Map<string, ContributionDto[]>();
  for (const c of contributions ?? []) {
    const key = evidenceGroupKey(c);
    const members = groups.get(key);
    if (members) members.push(c); else groups.set(key, [c]);
  }

  const result: EvidenceGroupVm[] = [];
  for (const members of groups.values()) {
    const representative = members.reduce((best, c) =>
      Date.parse(c.lastFiredAt || '') >= Date.parse(best.lastFiredAt || '') ? c : best);
    const occurrences = members.reduce((sum, c) => sum + Math.max(c.fireCount ?? 0, 1), 0);
    const hasLive = members.some(c => !c.closure?.reason);
    const firstTimes = members.map(c => c.firstFiredAt).filter(t => !isUnsetDate(t)).map(Date.parse);
    const seenTimes = members.map(c => !isUnsetDate(c.lastCorroboratedAt) ? c.lastCorroboratedAt : c.lastFiredAt)
      .filter(t => !isUnsetDate(t)).map(Date.parse);
    result.push({ representative: { ...representative,
      firstFiredAt: firstTimes.length ? new Date(Math.min(...firstTimes)).toISOString() : '',
      lastCorroboratedAt: seenTimes.length ? new Date(Math.max(...seenTimes)).toISOString() : '',
    }, occurrences, hasLive });
  }

  const alertFirst = (g: EvidenceGroupVm) =>
    g.representative.sourceType === ContributionSourceTypes.Alert ? 0 : 1;
  return result.sort((a, b) =>
    alertFirst(a) - alertFirst(b)
    || (b.representative.metrics?.currentDecayedWeight ?? 0)
    - (a.representative.metrics?.currentDecayedWeight ?? 0));
}

// An operator report's author lives in the activity feed, not on the contribution.
// The writer links exactly one attributable row per report through
// targetId = contributionId — issue_created on a create, ReportAdded on a merge,
// Reopened on a reopen — and those three outcomes are mutually exclusive, so a
// contribution never has more than one row. Matching on targetId alone therefore
// covers all three kinds with one rule, and cannot confuse a report with a
// disposition close: that writes a user-initiated row too, but targets no
// contribution. Only Manual contributions are linked; machine rows leave targetId at
// its empty default, which is why a contribution with no id is skipped rather than
// allowed to match every unlinked row.
//
// The link is written even when the report carried no actor (the row is then
// system-authored), so a match still has to prove a real user before a name is shown.
// Returns contributionId -> display name for the reports we can name; anything left
// out falls back to the unattributed wording.
export function manualReporterNames(
  issue: IssueDto,
  resolveName: (userId: string) => string | null,
): Record<string, string> {
  const names: Record<string, string> = {};
  const activity = issue?.activity ?? [];
  if (!activity.length) return names;

  for (const c of issue?.contributions ?? []) {
    if (c.sourceType !== ContributionSourceTypes.Manual || !c.contributionId) continue;
    const row = activity.find(a =>
      a.targetId === c.contributionId
      && a.initiatedBy === 'User'
      && !!a.userId
      && a.userId !== SYSTEM_ACTOR);
    if (!row) continue;
    const name = resolveName(row.userId);
    if (name) names[c.contributionId] = name;
  }

  return names;
}

// Disposition-set activity line keys (the whole line is translated, no nested params).
const DISPOSITION_ACTIVITY_KEYS: Record<DispositionType, string> = {
  RealAndFixed: '@issueActivityDispositionRealAndFixed',
  FalsePositive: '@issueActivityDispositionFalsePositive',
  Dismissed: '@issueActivityDispositionDismissed',
  Duplicate: '@issueActivityDispositionDuplicate',
  AutoClosed: '@issueActivityDispositionAutoClosed',
};

// The drawer's wording for the same events: what happened, in the reader's words, with the
// reason after a dot. The classic hosts keep the older keys.
const PLAIN_REASON_KEYS: Record<StateReasonCode, string> = {
  issue_created: '@issueHistoryOpened',
  reopen: '@issueHistoryReopenedPlain',
  confidence_crossed_active_threshold: '@issueHistoryBecameActive',
  confidence_dropped_below_active_threshold: '@issueHistoryStartedFading',
  confidence_recovered_to_active_threshold: '@issueHistoryActiveAgain',
  dvir_repaired_auto_close: '@issueHistoryDvirRepaired',
};

function plainActivityTextKey(a: ActivityEntryDto): string {
  switch (a.kind) {
    case ActivityKinds.Reopened: return '@issueHistoryReopenedPlain';
    case ActivityKinds.ClearCanceled: return '@issueHistoryClearCanceled';
    case ActivityKinds.LifecycleChanged:
      return PLAIN_REASON_KEYS[a.reason as StateReasonCode] ?? '@issueHistoryStateChanged';
    default: return activityTextKey(a);
  }
}

function activityTextKey(a: ActivityEntryDto): string {
  switch (a.kind) {
    case ActivityKinds.Assigned: return '@issueActivityAssigned';
    case ActivityKinds.Unassigned: return '@issueActivityUnassigned';
    case ActivityKinds.Reopened: return '@issueReasonReopened';
    case ActivityKinds.ReportAdded: return '@issueActivityReportAdded';
    case ActivityKinds.ClearReceived: return '@issueActivityClearReceived';
    case ActivityKinds.ClearCanceled: return '@issueActivityClearCanceled';
    case ActivityKinds.DescriptionChanged: return '@issueActivityDescriptionChanged';
    case ActivityKinds.DispositionSet:
      return DISPOSITION_ACTIVITY_KEYS[a.toValue as DispositionType] ?? '@issueReasonGeneric';
    case ActivityKinds.LifecycleChanged: return reasonKey(a.reason);
    default: return '@issueReasonGeneric';
  }
}

function activityTextParams(
  a: ActivityEntryDto,
  resolveName: (userId: string) => string | null,
): Record<string, unknown> | undefined {
  if (a.kind === ActivityKinds.Assigned) {
    return { name: resolveName(a.toValue) || a.toValue };
  }
  if (a.kind === ActivityKinds.DescriptionChanged) {
    return { to: a.toValue };
  }
  return undefined;
}

// Issue activity timeline for the expanded card, oldest first (newest at the
// bottom) — lifecycle, reopen, disposition, and one row per operator report that
// merged in (a merge is otherwise invisible here). Notes no longer exist on the Issue
// (comments live on the linked Case's feed); historical note add/delete
// activity rows are skipped. Everything else renders as a translated event
// line attributed to system / user / external.
export function buildTimeline(
  issue: IssueDto,
  resolveName: (userId: string) => string | null,
  options: { plain?: boolean } = {},
): IssueTimelineEntryVm[] {
  const rows = (issue.activity ?? [])
    .filter(a => a.kind !== ActivityKinds.NoteAdded && a.kind !== ActivityKinds.NoteDeleted)
    // An operator disposition emits both a DispositionSet ("Closed — …") and a
    // LifecycleChanged(reason=disposition_applied); the DispositionSet row already
    // conveys the close, so skip the redundant lifecycle row.
    .filter(a => !(a.kind === ActivityKinds.LifecycleChanged && a.reason === 'disposition_applied'))
    .filter(a => !isUnsetDate(a.occurredAtDateTimeUtc))
    // Ascending (oldest first) so the newest entry sits at the bottom, the
    // SMS/chat ordering the Cases feed uses.
    .sort((a, b) => Date.parse(a.occurredAtDateTimeUtc) - Date.parse(b.occurredAtDateTimeUtc));

  const entries: IssueTimelineEntryVm[] = [];

  for (const a of rows) {
    const isUser = !!a.userId && a.userId !== SYSTEM_ACTOR;
    const textKey = options.plain ? plainActivityTextKey(a) : activityTextKey(a);
    const textParams = activityTextParams(a, resolveName);

    entries.push({
      id: a.id,
      kind: a.kind,
      atUtc: a.occurredAtDateTimeUtc,
      initiatedBy: a.initiatedBy,
      actorUserId: isUser ? a.userId : null,
      actorName: isUser ? resolveName(a.userId) : null,
      notable: a.kind === ActivityKinds.Reopened || a.reason === 'confidence_crossed_active_threshold',
      textKey,
      textParams,
    });
  }

  return entries;
}

// How long something has been open, at the resolution anyone acts on. Under a day reads as
// the hours, so a fresh Issue never claims to be zero days old. Shared because the row and
// the group header both state it, and two copies drift.
export function elapsedLabel(sinceUtc: string | undefined): string {
  const openedMs = Date.parse(sinceUtc ?? '');
  if (!openedMs) return '';
  const hours = Math.floor((Date.now() - openedMs) / 3_600_000);
  if (hours < 1) return '<1h';
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}
